import { html, type TemplateResult } from 'lit';

/**
 * SVG-iconen, gecentraliseerd zodat:
 *   1. dezelfde icoon (bv. close, attach) niet 3x gedupliceerd staat in
 *      verschillende components met afwijkende sizes/strokes
 *   2. design-tokens (stroke-width, viewBox-conventie) één bron krijgen
 *   3. icon-vervanging één plek raakt
 *
 * Conventie: alle iconen `fill="none"`, `stroke="currentColor"`, met
 * een default stroke-width per icoon. `width` en `height` zijn de
 * default — kan via een wrapper-element overruled worden via CSS
 * (alle iconen erven currentColor).
 *
 * Functies retourneren een Lit `TemplateResult` zodat aanroepers ze in
 * hun eigen template kunnen interpoleren: `${iconClose()}`.
 */

const SVG_BASE =
  'fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';

// ───────────────────────── header / panel-controls ─────────────────────────

export function iconBack(): TemplateResult {
  return html`
    <svg viewBox="0 0 24 24" stroke-width="2.2" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="m15 18-6-6 6-6" />
    </svg>
  `;
}

export function iconClose(size = 14): TemplateResult {
  return html`
    <svg width=${size} height=${size} viewBox="0 0 24 24" stroke-width="2.2" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  `;
}

export function iconDownload(): TemplateResult {
  return html`
    <svg width="20" height="20" viewBox="0 0 24 24" stroke-width="1.9" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 4v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M5 20h14" />
    </svg>
  `;
}

export function iconChevronDown(): TemplateResult {
  return html`
    <svg viewBox="0 0 24 24" stroke-width="2.2" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  `;
}

export function iconChatBubble(): TemplateResult {
  // Solid tekstballon met drie typing-dots als uitsnedes. Eén path met
  // fill-rule="evenodd": de bubble (gesloten subpath) krijgt parity 1
  // (gevuld), de drie dot-subpaths binnen de bubble krijgen parity 2
  // (uitgesneden). Geen mask/defs nodig — id-collisions vermeden bij
  // meerdere widget-instances op dezelfde pagina.
  //
  // Optische centrering: tail-driehoek aan de onderkant trekt het
  // zwaartepunt naar onderen, dus bubble iets boven het viewBox-midden
  // geplaatst (y=3..14) zodat het icoon-geheel optisch op (12,12) valt.
  return html`
    <svg viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" aria-hidden="true">
      <path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-3 3.4a.6.6 0 0 1-1.05-.4V17H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm3 5.5a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4zm4 0a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4zm4 0a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4z"/>
    </svg>
  `;
}

// ─────────────────────────── composer ───────────────────────────

export function iconAttach(size = 18): TemplateResult {
  return html`
    <svg width=${size} height=${size} viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-8.49 8.49a1.5 1.5 0 0 1-2.12-2.12l7.78-7.78"/>
    </svg>
  `;
}

export function iconSend(): TemplateResult {
  return html`
    <svg width="16" height="16" viewBox="0 0 24 24" stroke-width="2.2" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  `;
}

export function iconImage(): TemplateResult {
  return html`
    <svg width="14" height="14" viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2"/>
      <circle cx="9" cy="10" r="1.5"/>
      <path d="m21 16-5-5-9 9"/>
    </svg>
  `;
}

export function iconSmile(): TemplateResult {
  return html`
    <svg width="18" height="18" viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <path d="M9 9h.01M15 9h.01" />
    </svg>
  `;
}

// ─────────────────────────── message-list ───────────────────────────

export function iconCheck(): TemplateResult {
  return html`
    <svg width="12" height="12" viewBox="0 0 24 24" stroke-width="2.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M5 13l4 4L19 7"/>
    </svg>
  `;
}

export function iconCheckDouble(): TemplateResult {
  return html`
    <svg width="14" height="12" viewBox="0 0 24 18" stroke-width="2.4" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M2 9l4 4 8-10"/>
      <path d="M10 13l3 3 9-11"/>
    </svg>
  `;
}

// ─────────────────────────── status-hero / offline ───────────────────────────

export function iconClock(): TemplateResult {
  return html`
    <svg width="34" height="34" viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  `;
}

export function iconChat(): TemplateResult {
  return html`
    <svg width="32" height="32" viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  `;
}

export function iconInfo(): TemplateResult {
  return html`
    <svg width="32" height="32" viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v4h1" />
    </svg>
  `;
}

// ─────────────────────────── settings-menu ───────────────────────────

export function iconMore(): TemplateResult {
  return html`
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  `;
}

export function iconUser(): TemplateResult {
  return html`
    <svg width="20" height="20" viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  `;
}

export function iconLogout(): TemplateResult {
  return html`
    <svg width="20" height="20" viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  `;
}

export function iconTrash(): TemplateResult {
  return html`
    <svg width="20" height="20" viewBox="0 0 24 24" stroke-width="1.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  `;
}

// SVG_BASE is voorlopig ongebruikt — bewaard voor later wanneer alle
// icons exact dezelfde stroke-width delen. Voor nu houdt elk icoon zijn
// eigen stroke-width omdat ze visueel anders gewogen zijn.
void SVG_BASE;
